import React, {
  useCallback,
  useEffect,
  useMemo,
  useRef,
  useState,
} from "react";

import {
  AlertCircle,
  BookOpen,
  ChevronUp,
  Loader2,
  Mic,
  MicOff,
  X,
} from "lucide-react";

import Badge from "../../Components/Badge";
import Button from "../../Components/Button";
import CommonModal from "../../Components/Modal";
import MultiSelect from "../../Components/MultiSelect";
import RadioButton from "../../Components/RadioButton";
import Select from "../../Components/Select";
import Table from "../../Components/Table";
import TextArea from "../../Components/TextArea";
import useSpeechToText from "../../Hooks/useSpeechToText";
import { useAppDispatch, useAppSelector } from "../../Store/Hooks";
import { fetchOrganizationLevels } from "../../Store/Reducers/AdminSlice";
import type {
  Question,
  QuestionOption,
} from "../../Store/Reducers/AssessmentSlice";
import {
  formatAssessmentDateTimePartsUtc,
  formatAssessmentRelativeTimeAgoUtc,
} from "../../Utils/Helper/assessmentDateUtils";
import type { TeamPeerBanner } from "../../Utils/Helper/assessmentOriginHelpers";
import {
  getAccountLevelVerboseLabel,
  getLevelCompactName,
} from "../../Utils/Helper/levelUtils";

// --- Types -----------------------------------------------------------------
export type AssessmentTemplateQuestionOption = QuestionOption;

export type AssessmentQuestion = Question & {
  id: string;
  type?: "dropdown" | "textarea" | "radio";
  placeholder?: string | null;
  options?: AssessmentTemplateQuestionOption[] | null;
  categoryName?: string | null;
};

type AssessmentAccount = { id?: string | null; name?: string | null };

export type AssessmentForReview = {
  id: string;
  title: string;
  status: string;
  account?: AssessmentAccount | null;
  questions: AssessmentQuestion[];
};

export type VoiceModeConfig = {
  enabled: boolean;
  supported: boolean;
  isListening: boolean;
  onToggle: (next: boolean) => void;
  showInstructions?: boolean;
};

export type AssessmentResponseMap = Record<
  string,
  string | string[] | undefined
>;

export type TemplateData = {
  id: string;
  name: string;
  json_schema: any;
  status?: string;
};

// --- Props -----------------------------------------------------------------

type ReviewAssessmentModalProps = {
  isOpen: boolean;
  templateData?: TemplateData | null;
  onClose: () => void;
  responses: Record<string, AssessmentResponseMap>;
  onChange: (questionId: string, value: string | string[]) => void;
  onSaveDraft: () => void;
  onSubmit: () => void;
  isSubmitting: boolean;
  voiceMode: VoiceModeConfig;
  currentQuestionIndex?: number;
  onQuestionIndexChange?: (index: number) => void;
  accountName?: string | null;
  isPreviewMode?: boolean;
  /**
   * Force view-only (e.g. Self Completed tab). Formal Completed stays editable
   * until published — do not set this for that case.
   */
  forceReadOnly?: boolean;
  /** Portfolio self-assessment: Cancel / Save draft / Go to My Assessments */
  footerVariant?: "default" | "portfolio";
  onGoToMyAssessments?: () => void;
  /** When false, Go to My Assessments is shown disabled (no draft/assigned for account). */
  canGoToMyAssessments?: boolean;
  portfolioSubmitAction?: "draft" | "submit" | "navigate" | null;
  /** Shown when formal AL assessment is pending; disables portfolio actions */
  portfolioBlockedMessage?: string | null;
  isPortfolioValidationLoading?: boolean;
  /** Opens formal assessment flow when self-serve is blocked by pending AL work */
  onTakeFormalAssessment?: () => void;
  isTakeFormalAssessmentLoading?: boolean;
  /** After prior response found: carry forward vs re-assess */
  portfolioAssessMode?: "carry_forward" | "re_assess" | null;
  portfolioFormFlow?: "default" | "continued";
  carryForwardNote?: string;
  onCarryForwardNoteChange?: (note: string) => void;
  onFormBack?: () => void;
  portfolioStatusBadge?: string | null;
  /** S4: a running Formal is open on this account and this submit folds into it. */
  isFormalFoldIn?: boolean;
  /** True when resuming or after saving a draft. */
  isDraftSession?: boolean;
  /** Team assessment: a teammate already submitted on this round. */
  teamPeerBanner?: TeamPeerBanner | null;
  /** Formal/Self re-assess: remind user they can still change answers until due/publish. */
  reassessBanner?: {
    submittedAt?: string | null;
    dueDate?: string | null;
  } | null;
  /** No window open: the input is kept privately until the next close. */
  remainderBanner?: { countsOn?: string | null } | null;
};

const renderReassessBannerMessage = (banner: {
  submittedAt?: string | null;
  dueDate?: string | null;
}): React.ReactNode => {
  const relative =
    formatAssessmentRelativeTimeAgoUtc(banner.submittedAt) ||
    (banner.submittedAt
      ? `on ${formatAssessmentDateTimePartsUtc(banner.submittedAt, "DMY_SHORT").date}`
      : null);
  const dueLabel = banner.dueDate
    ? formatAssessmentDateTimePartsUtc(banner.dueDate, "DMY_SHORT").date
    : null;

  return (
    <>
      You already submitted this assessment
      {relative ? (
        <>
          {" "}
          <strong>{relative}</strong>
        </>
      ) : null}
      . You can change your responses
      {dueLabel && dueLabel !== "-" ? (
        <>
          {" "}
          until the due date (<strong>{dueLabel}</strong>)
        </>
      ) : (
        <> until the due date</>
      )}
      .
    </>
  );
};

export const extractQuestionsFromJsonSchema = (
  jsonSchema: any
): AssessmentQuestion[] => {
  if (!jsonSchema || typeof jsonSchema !== "object") return [];

  const questionsWithCategory: Array<{ q: any; categoryName?: string | null }> =
    [];

  if (Array.isArray(jsonSchema.categories)) {
    jsonSchema.categories.forEach((cat: any) => {
      if (!Array.isArray(cat.questions)) return;
      cat.questions.forEach((q: any) =>
        questionsWithCategory.push({
          q,
          categoryName: cat.category_name ?? null,
        })
      );
    });
  } else if (Array.isArray(jsonSchema.questions)) {
    jsonSchema.questions.forEach((q: any) =>
      questionsWithCategory.push({ q, categoryName: null })
    );
  }

  return questionsWithCategory
    .filter(({ q }) => q)
    .sort(
      (a, b) => (Number(a.q.order_index) || 0) - (Number(b.q.order_index) || 0)
    )
    .map(({ q, categoryName }) => {
      const options: AssessmentTemplateQuestionOption[] | undefined =
        q.options && q.options.choices && Array.isArray(q.options.choices)
          ? q.options.choices.map((choice: any) => ({
              label: choice.label ?? choice.value ?? "",
              value: choice.value ?? choice.label ?? "",
            }))
          : undefined;

      return {
        id:
          q.id ??
          `question-${q.order_index ?? Math.random().toString(36).slice(2)}`,
        question: q.question_text ?? q.question ?? "",
        questionType: q.question_type,
        required: Boolean(q.required ?? true),
        options,
        categoryName: categoryName ?? null,
      } as AssessmentQuestion;
    });
};

// --- Subcomponents --------------------------------------------------------

const XFactorTable: React.FC<{
  selectedLevel?: string | null;
  organizationLevels: Array<{
    level_code: string;
    name: string;
    description: string;
  }>;
}> = ({ selectedLevel, organizationLevels }) => {
  const tableData = organizationLevels.map(level => ({
    level: level.level_code,
    name: level.name,
    description: level.description,
  }));

  const selectedLevelData = organizationLevels.find(
    l => l.level_code === selectedLevel
  );

  return (
    <div className="mb-6">
      <Table
        columns={[
          {
            key: "level",
            title: "AL",
            width: "80px",
            render: (value: string) => getLevelCompactName(value),
          },
          { key: "name", title: "Name", width: "160px" },
          { key: "description", title: "Description" },
        ]}
        data={tableData}
        rowKey={(r: { level: string }) => r.level}
        className="mb-4"
      />
      {selectedLevel && selectedLevelData ? (
        <Badge
          label={`Selected: ${getAccountLevelVerboseLabel(selectedLevel, selectedLevelData.name)}`}
          type="long-assessment-purple"
          className="assessment-type-badge"
        />
      ) : null}
    </div>
  );
};

const FALLBACK_GUIDANCE =
  "Provide your rationale and key evidence that supports this level rating.";

const InputRenderer: React.FC<{
  question: AssessmentQuestion;
  value: string | string[] | undefined;
  onChange: (value: string | string[]) => void;
  disabled?: boolean;
  organizationLevels: Array<{
    level_code: string;
    name: string;
    description: string;
    guidance?: string;
  }>;
  onGuidanceChange?: (guidance: string) => void;
  placeholderOverride?: string;
}> = ({
  question,
  value,
  onChange,
  disabled,
  organizationLevels,
  onGuidanceChange,
  placeholderOverride,
}) => {
  const [rationaleGuidance, setRationaleGuidance] = useState<string>("");
  const [showGuidanceDrawer, setShowGuidanceDrawer] = useState<boolean>(false);

  // Check if question options match organization levels (dynamic XFactor check)
  const questionOptionValues = (question.options ?? []).map(opt =>
    String(opt.value)
  );
  const organizationLevelCodes = organizationLevels.map(l => l.level_code);
  const isXfactorQuestion = questionOptionValues.some(optValue =>
    organizationLevelCodes.includes(optValue)
  );

  // Dynamic XFactor options from organization levels (not from question.options)
  const xfactorOptions =
    organizationLevels.map(l => ({
      label: l.name,
      value: l.level_code,
    })) ||
    question.options ||
    [];
  const xfactorSelected = String(value ?? "");

  // Show guidance drawer when a level is pre-selected (e.g. opening an existing assessment)
  useEffect(() => {
    if (!isXfactorQuestion && question.id !== "maturity_level") return;
    if (!xfactorSelected || !organizationLevels.length) return;
    const level = organizationLevels.find(
      l => l.level_code === xfactorSelected
    );
    if (level) {
      const guidance = level.guidance || FALLBACK_GUIDANCE;
      setRationaleGuidance(guidance);
      setShowGuidanceDrawer(true);
      onGuidanceChange?.(guidance);
    }
  }, [
    xfactorSelected,
    organizationLevels.length,
    isXfactorQuestion,
    question.id,
    organizationLevels,
    onGuidanceChange,
  ]);

  if (isXfactorQuestion || question.id === "maturity_level") {
    return (
      <div className="space-y-4">
        <div className={"bg-blue-50 border border-blue-200 rounded-lg p-3"}>
          <p className="text-sm text-gray-600 mb-2">
            Reference:{" "}
            <span className="font-semibold text-gray-900">
              XFACTOR™ Account Level Definitions
            </span>
          </p>
          <XFactorTable
            selectedLevel={xfactorSelected || undefined}
            organizationLevels={organizationLevels}
          />
        </div>
        <div className="flex flex-nowrap gap-2 overflow-x-auto pb-1">
          {xfactorOptions.map(opt => {
            const levelData = organizationLevels.find(
              l => l.level_code === String(opt.value)
            );
            const isSelected = xfactorSelected === String(opt.value);
            return (
              <div
                key={`${question.id}-${opt.value}`}
                className={`min-w-[7.5rem] flex-1 p-2.5 rounded-lg border transition-all ${
                  isSelected
                    ? "border-[#974ae4] bg-[#974ae4]/5 shadow-sm"
                    : "border-gray-200 hover:border-gray-300"
                }`}
              >
                <RadioButton
                  name={`${question.id}`}
                  value={String(opt.value)}
                  label={
                    <span className="flex flex-col">
                      <span className="font-medium text-gray-900">
                        {getLevelCompactName(String(opt.value))}
                      </span>
                      <span className="text-sm text-gray-600">
                        {levelData?.name ?? opt.label}
                      </span>
                    </span>
                  }
                  checked={isSelected}
                  onChange={e => {
                    const selected = e.target.value;
                    onChange(selected);
                    const level = organizationLevels.find(
                      l => l.level_code === selected
                    );
                    const guidance = level?.guidance || FALLBACK_GUIDANCE;
                    setRationaleGuidance(guidance);
                    setShowGuidanceDrawer(true);
                    onGuidanceChange?.(guidance);
                  }}
                  disabled={disabled}
                />
              </div>
            );
          })}
        </div>
        <p className="text-xs text-gray-500">
          Choose a level from customer conversations and evidence. If you are
          unsure, talk to the customer and pick at least AL-0 or AL-1.
        </p>
        {xfactorSelected && (
          <div>
            <button
              type="button"
              onClick={() => setShowGuidanceDrawer(v => !v)}
              className="flex items-center gap-1.5 text-sm font-medium text-[#4f46e5] mb-2 hover:opacity-80 transition-opacity"
            >
              <BookOpen size={14} />
              Rationale guidance
              <ChevronUp
                size={14}
                className={`transition-transform ${showGuidanceDrawer ? "" : "rotate-180"}`}
              />
            </button>
            {showGuidanceDrawer && (
              <div className="rounded-xl border border-[#e0e0f0] bg-[#eef0fb] px-4 py-3 text-sm text-[#3730a3]">
                {rationaleGuidance}
              </div>
            )}
          </div>
        )}
      </div>
    );
  }

  if (question.questionType === "single_choice") {
    return (
      <Select
        name={question.id}
        value={String(value ?? "")}
        placeholder={question.placeholder ?? "Select an option..."}
        options={question.options ?? []}
        onChange={e => onChange(e.target.value)}
        disabled={disabled}
        isShowPlaceHolder
      />
    );
  }

  if (question.questionType === "free_text") {
    return (
      <div className="w-full">
        <TextArea
          name={question.id}
          value={String(value ?? "")}
          placeholder={placeholderOverride || question.placeholder || ""}
          onChange={e => onChange(e.target.value)}
          disabled={disabled}
          rows={4}
          className="w-full"
        />
      </div>
    );
  }

  if (question.questionType === "multiple_choice") {
    const selectedValues = Array.isArray(value) ? value : value ? [value] : [];
    const options = (question.options ?? []).map(opt => ({
      label: String(opt.label),
      value: String(opt.value),
    }));

    const selectedOptions = options.filter(opt =>
      selectedValues.includes(opt.value)
    );

    const handleMultiSelectChange = (
      selected: Array<{ label: string; value: string }>
    ) => {
      onChange(selected.map(opt => opt.value));
    };

    const handleDisabledChange = (
      _selected: Array<{ label: string; value: string }>
    ) => {
      // No-op: disabled in preview mode
    };

    return (
      <div
        className={`w-full ${disabled ? "pointer-events-none opacity-60 select-none" : ""}`}
      >
        <MultiSelect
          options={options}
          selected={selectedOptions}
          onChange={disabled ? handleDisabledChange : handleMultiSelectChange}
          placeholder={question.placeholder ?? "Select options..."}
          showAvatar={false}
        />
      </div>
    );
  }

  // Default radio button rendering for non-XFactor questions
  const options = question.options ?? [];
  const selected = String(value ?? "");

  return (
    <div className="space-y-3">
      <div className="flex flex-col gap-3">
        {options.map(opt => {
          const isSelected = selected === String(opt.value);
          return (
            <div
              key={`${question.id}-${opt.value}`}
              className={`p-3 rounded-lg border transition-all ${
                isSelected
                  ? "border-[#974ae4] bg-[#974ae4]/5 shadow-sm"
                  : "border-gray-200 hover:border-gray-300"
              }`}
            >
              <RadioButton
                name={`${question.id}`}
                value={String(opt.value)}
                label={String(opt.label)}
                checked={isSelected}
                onChange={e => onChange(e.target.value)}
                disabled={disabled}
              />
            </div>
          );
        })}
      </div>
    </div>
  );
};

// --- Main Component -------------------------------------------------------

const ReviewAssessmentModal: React.FC<ReviewAssessmentModalProps> = ({
  isOpen,
  templateData,
  onClose,
  responses,
  onChange,
  onSaveDraft,
  onSubmit,
  isSubmitting,
  voiceMode,
  currentQuestionIndex = 0,
  onQuestionIndexChange,
  accountName: accountNameProp,
  isPreviewMode = false,
  forceReadOnly = false,
  footerVariant = "default",
  onGoToMyAssessments,
  canGoToMyAssessments = true,
  portfolioSubmitAction = null,
  portfolioBlockedMessage = null,
  isPortfolioValidationLoading = false,
  onTakeFormalAssessment,
  isTakeFormalAssessmentLoading = false,
  portfolioAssessMode = null,
  portfolioFormFlow = "default",
  carryForwardNote = "",
  onCarryForwardNoteChange,
  onFormBack,
  portfolioStatusBadge = null,
  isFormalFoldIn = false,
  isDraftSession = false,
  teamPeerBanner = null,
  reassessBanner = null,
  remainderBanner = null,
}) => {
  const dispatch = useAppDispatch();
  const { organizationLevels } = useAppSelector((state: any) => state.admin);
  const [activeGuidance, setActiveGuidance] = useState<string>("");

  // Speech-to-text hook
  const {
    isListening,
    transcript,
    error: speechError,
    isSupported,
    startListening,
    stopListening,
    resetTranscript,
    processVoiceCommand,
    clearError,
  } = useSpeechToText();

  const lastProcessedTranscriptRef = useRef<string>("");
  const transcriptTimeoutRef = useRef<NodeJS.Timeout | null>(null);

  const finalAssessment = useMemo(() => {
    if (!templateData) return null;
    const questions = extractQuestionsFromJsonSchema(templateData.json_schema);
    return {
      id: templateData.id,
      title: templateData.name ?? "Template Preview",
      status: templateData.status,
      account: accountNameProp ? { name: accountNameProp } : null,
      questions,
    } as AssessmentForReview;
  }, [templateData, accountNameProp]);

  const getResponseValue = useCallback(
    (questionId: string) => {
      // Formal pending: show empty Team form — do not surface prior Team answers.
      if (portfolioBlockedMessage) {
        return "";
      }
      return responses?.[finalAssessment?.id ?? ""]?.[questionId] ?? "";
    },
    [responses, finalAssessment, portfolioBlockedMessage]
  );

  // Check if all required fields are filled
  const areAllRequiredFieldsFilled = useMemo(() => {
    if (!finalAssessment || !finalAssessment.questions) {
      return false;
    }

    const requiredQuestions = finalAssessment.questions.filter(q => q.required);

    if (requiredQuestions.length === 0) {
      return true; // No required questions, so all are "filled"
    }

    return requiredQuestions.every(question => {
      const value = getResponseValue(question.id);

      // Check if value exists and is not empty
      if (value === undefined || value === null) {
        return false;
      }

      // For string values, check if not empty
      if (typeof value === "string") {
        return value.trim().length > 0;
      }

      // For array values (multiselect), check if array has at least one item
      if (Array.isArray(value)) {
        return value.length > 0;
      }

      // For other types, just check if truthy
      return !!value;
    });
  }, [finalAssessment, getResponseValue, responses]);

  const hasAnyResponse = useMemo(() => {
    if (!finalAssessment?.questions) {
      return false;
    }

    return finalAssessment.questions.some(question => {
      const value = getResponseValue(question.id);

      if (value === undefined || value === null) {
        return false;
      }

      if (typeof value === "string") {
        return value.trim().length > 0;
      }

      if (Array.isArray(value)) {
        return value.length > 0;
      }

      return !!value;
    });
  }, [finalAssessment, getResponseValue, responses]);

  const statusNorm = (finalAssessment?.status || "").toLowerCase();
  const isPublished = statusNorm === "published";
  const isPrePublish = statusNorm === "pre_publish";
  // Formal COMPLETED stays editable until published. Self Completed / published
  // / leftover PRE_PUBLISH use forceReadOnly or status locks below.
  const isLockedForViewing =
    forceReadOnly || isPublished || isPrePublish || isPreviewMode;
  const isPortfolioBlocked = Boolean(portfolioBlockedMessage);
  const isCarryForwardMode = portfolioAssessMode === "carry_forward";
  const isContinuedFlow = portfolioFormFlow === "continued";

  const isQuestionReadOnly = useCallback(
    (questionId: string) =>
      isCarryForwardMode &&
      (questionId === "maturity_level" ||
        questionId === "rationale" ||
        questionId.includes("maturity")),
    [isCarryForwardMode]
  );

  const questionsDisabled = isLockedForViewing || isPortfolioBlocked;
  const isAborted = finalAssessment?.status === "aborted";

  // Fetch organization levels when modal opens if not already loaded
  useEffect(() => {
    if (!isOpen || organizationLevels.length > 0) {
      return;
    }
    dispatch(fetchOrganizationLevels());
  }, [isOpen, dispatch, organizationLevels.length]);

  // Sync voice mode with speech recognition
  useEffect(() => {
    if (
      voiceMode.enabled &&
      isSupported &&
      !isListening &&
      !isLockedForViewing
    ) {
      startListening();
    } else if ((!voiceMode.enabled || isLockedForViewing) && isListening) {
      stopListening();
      resetTranscript();
    }
  }, [
    voiceMode.enabled,
    isSupported,
    isListening,
    isLockedForViewing,
    startListening,
    stopListening,
    resetTranscript,
  ]);

  // Handle voice commands and transcript updates
  useEffect(() => {
    if (!voiceMode.enabled || !transcript || isLockedForViewing) {
      return;
    }

    // If transcript was reset (empty), reset our tracking
    if (transcript.length < lastProcessedTranscriptRef.current.length) {
      lastProcessedTranscriptRef.current = "";
    }

    // Get only the new part of the transcript
    const processedLength = lastProcessedTranscriptRef.current.length;
    const newTranscript = transcript.slice(processedLength).trim();

    // If no new transcript, skip processing
    if (!newTranscript) {
      return;
    }

    // Clear any existing timeout
    if (transcriptTimeoutRef.current) {
      clearTimeout(transcriptTimeoutRef.current);
    }

    // Wait a bit for the transcript to stabilize before processing
    transcriptTimeoutRef.current = setTimeout(() => {
      // Capture current values at the time of execution
      // Use a function to get the latest transcript value
      const currentTranscript = transcript;
      const currentProcessedLength = lastProcessedTranscriptRef.current.length;

      // If transcript was reset, adjust
      if (currentTranscript.length < currentProcessedLength) {
        lastProcessedTranscriptRef.current = "";
      }

      const currentNewTranscript = currentTranscript
        .slice(lastProcessedTranscriptRef.current.length)
        .trim();

      // Double-check we still have new transcript
      if (!currentNewTranscript || currentNewTranscript.length === 0) {
        return;
      }

      // Update tracking BEFORE processing (to avoid duplicate processing)
      lastProcessedTranscriptRef.current = currentTranscript;

      // Process voice command - use only the new part
      const command = processVoiceCommand(currentNewTranscript);

      if (command) {
        switch (command.type) {
          case "NEXT_QUESTION":
            if (finalAssessment && onQuestionIndexChange) {
              const nextIndex = Math.min(
                currentQuestionIndex + 1,
                finalAssessment.questions.length - 1
              );
              onQuestionIndexChange(nextIndex);
              resetTranscript();
              lastProcessedTranscriptRef.current = "";
            }
            break;

          case "PREVIOUS_QUESTION":
            if (onQuestionIndexChange) {
              const prevIndex = Math.max(currentQuestionIndex - 1, 0);
              onQuestionIndexChange(prevIndex);
              resetTranscript();
              lastProcessedTranscriptRef.current = "";
            }
            break;

          case "SUBMIT":
            if (!isSubmitting && !isPreviewMode && areAllRequiredFieldsFilled) {
              onSubmit();
              resetTranscript();
              lastProcessedTranscriptRef.current = "";
            }
            break;

          case "SAVE_DRAFT":
            if (
              !isSubmitting &&
              !isPreviewMode &&
              !isLockedForViewing &&
              hasAnyResponse
            ) {
              onSaveDraft();
              resetTranscript();
              lastProcessedTranscriptRef.current = "";
            }
            break;

          case "CLEAR_RESPONSE": {
            if (finalAssessment) {
              const currentQuestion =
                finalAssessment.questions[currentQuestionIndex];
              if (currentQuestion) {
                onChange(currentQuestion.id, "");
                resetTranscript();
                lastProcessedTranscriptRef.current = "";
              }
            }
            break;
          }

          case "SELECT_RATING": {
            if (finalAssessment) {
              const currentQuestion =
                finalAssessment.questions[currentQuestionIndex];
              if (currentQuestion && currentQuestion.options) {
                // Find option that matches the rating value
                const ratingValue = String(command.value);
                const matchingOption = currentQuestion.options.find(
                  opt =>
                    String(opt.value) === ratingValue ||
                    String(opt.label) === ratingValue
                );
                if (matchingOption) {
                  onChange(currentQuestion.id, String(matchingOption.value));
                  resetTranscript();
                  lastProcessedTranscriptRef.current = "";
                }
              }
            }
            break;
          }

          case "SELECT_LEVEL": {
            // Handle level selection for XFactor questions
            if (finalAssessment && command.value) {
              const levelCode = String(command.value);
              const currentQuestion =
                finalAssessment.questions[currentQuestionIndex];
              if (currentQuestion) {
                // Check if this is an XFactor question
                const questionOptionValues = (
                  currentQuestion.options ?? []
                ).map(opt => String(opt.value));
                const organizationLevelCodes = organizationLevels.map(
                  (l: { level_code: string }) => l.level_code
                );
                const isXfactorQuestion = questionOptionValues.some(optValue =>
                  organizationLevelCodes.includes(optValue)
                );

                if (isXfactorQuestion) {
                  // Check if the level code exists in the question options
                  const matchingOption = currentQuestion.options?.find(
                    opt => String(opt.value) === levelCode
                  );
                  if (matchingOption) {
                    onChange(currentQuestion.id, levelCode);
                    resetTranscript();
                    lastProcessedTranscriptRef.current = "";
                  }
                }
              }
            }
            break;
          }

          case "SELECT_OPTION": {
            if (finalAssessment) {
              const currentQuestion =
                finalAssessment.questions[currentQuestionIndex];
              if (
                currentQuestion &&
                currentQuestion.options &&
                currentQuestion.options.length > 0
              ) {
                let selectedOption = null;

                // Handle by index if provided
                if (command.index !== undefined) {
                  if (command.index === -1) {
                    // "last" option
                    selectedOption =
                      currentQuestion.options[
                        currentQuestion.options.length - 1
                      ];
                  } else if (
                    command.index >= 0 &&
                    command.index < currentQuestion.options.length
                  ) {
                    selectedOption = currentQuestion.options[command.index];
                  }
                }

                // Try to match by label text from the transcript or command value
                if (!selectedOption) {
                  const searchText = String(
                    command.value || currentNewTranscript || ""
                  )
                    .toLowerCase()
                    .trim();

                  if (searchText) {
                    // Try exact match first
                    selectedOption = currentQuestion.options.find(opt => {
                      const label = String(opt.label).toLowerCase().trim();
                      const value = String(opt.value).toLowerCase().trim();
                      return label === searchText || value === searchText;
                    });

                    // Try partial match if no exact match
                    if (!selectedOption) {
                      selectedOption = currentQuestion.options.find(opt => {
                        const label = String(opt.label).toLowerCase();
                        const value = String(opt.value).toLowerCase();
                        // Check if search text contains the option or option contains search text
                        return (
                          label.includes(searchText) ||
                          searchText.includes(label) ||
                          value.includes(searchText) ||
                          searchText.includes(value)
                        );
                      });
                    }

                    // Try word-by-word matching for better accuracy
                    if (!selectedOption && searchText.length > 2) {
                      const searchWords = searchText
                        .split(/\s+/)
                        .filter((w: string) => w.length > 2);
                      selectedOption = currentQuestion.options.find(opt => {
                        const label = String(opt.label).toLowerCase();
                        const value = String(opt.value).toLowerCase();
                        // Check if most words match
                        const matchingWords = searchWords.filter(
                          (word: string) =>
                            label.includes(word) || value.includes(word)
                        );
                        return (
                          matchingWords.length >=
                            Math.min(2, searchWords.length) ||
                          (searchWords.length === 1 &&
                            (label.includes(searchWords[0]) ||
                              value.includes(searchWords[0])))
                        );
                      });
                    }
                  }
                }

                if (selectedOption) {
                  if (currentQuestion.questionType === "multiple_choice") {
                    // For multiselect, toggle the option
                    const currentValue = getResponseValue(
                      currentQuestion.id
                    ) as string[];
                    const currentValues = Array.isArray(currentValue)
                      ? currentValue
                      : currentValue
                        ? [currentValue]
                        : [];
                    const optionValue = String(selectedOption.value);
                    const newValues = currentValues.includes(optionValue)
                      ? currentValues.filter(v => v !== optionValue) // Remove if already selected
                      : [...currentValues, optionValue]; // Add if not selected
                    onChange(currentQuestion.id, newValues);
                  } else {
                    // For single choice (dropdown) or radio, just select it
                    onChange(currentQuestion.id, String(selectedOption.value));
                  }
                  resetTranscript();
                  lastProcessedTranscriptRef.current = "";
                }
              }
            }
            break;
          }

          case "TEXT_INPUT": {
            if (finalAssessment) {
              const currentQuestion =
                finalAssessment.questions[currentQuestionIndex];
              // Only apply to text areas (free_text questions)
              if (
                currentQuestion &&
                currentQuestion.questionType === "free_text" &&
                command.value
              ) {
                const currentValue = getResponseValue(
                  currentQuestion.id
                ) as string;
                const textValue = String(command.value);
                const newValue = currentValue
                  ? `${currentValue} ${textValue}`
                  : textValue;
                onChange(currentQuestion.id, newValue);
                // Don't reset transcript for text input - let it accumulate
                // But update tracking to avoid reprocessing
                lastProcessedTranscriptRef.current = currentTranscript;
              } else {
                // Not a text area, just update tracking
                lastProcessedTranscriptRef.current = currentTranscript;
              }
            }
            break;
          }

          default:
            // For other commands or unrecognized input, try option matching first, then text input
            if (finalAssessment) {
              const currentQuestion =
                finalAssessment.questions[currentQuestionIndex];

              // First, try to match as an option if it's a dropdown/multiselect
              if (
                currentQuestion &&
                (currentQuestion.questionType === "single_choice" ||
                  currentQuestion.questionType === "multiple_choice") &&
                currentQuestion.options
              ) {
                const searchText = currentNewTranscript.toLowerCase().trim();
                const matchingOption = currentQuestion.options.find(opt => {
                  const label = String(opt.label).toLowerCase();
                  const value = String(opt.value).toLowerCase();
                  return (
                    label === searchText ||
                    value === searchText ||
                    label.includes(searchText) ||
                    searchText.includes(label)
                  );
                });

                if (matchingOption) {
                  if (currentQuestion.questionType === "multiple_choice") {
                    const currentValue = getResponseValue(
                      currentQuestion.id
                    ) as string[];
                    const currentValues = Array.isArray(currentValue)
                      ? currentValue
                      : currentValue
                        ? [currentValue]
                        : [];
                    const optionValue = String(matchingOption.value);
                    const newValues = currentValues.includes(optionValue)
                      ? currentValues.filter(v => v !== optionValue)
                      : [...currentValues, optionValue];
                    onChange(currentQuestion.id, newValues);
                  } else {
                    onChange(currentQuestion.id, String(matchingOption.value));
                  }
                  resetTranscript();
                  lastProcessedTranscriptRef.current = "";
                  break; // Exit early
                }
              }

              // If not matched as option, treat as text input for text areas
              if (
                currentQuestion &&
                currentQuestion.questionType === "free_text"
              ) {
                const currentValue = getResponseValue(
                  currentQuestion.id
                ) as string;
                const newValue = currentValue
                  ? `${currentValue} ${currentNewTranscript}`
                  : currentNewTranscript;
                onChange(currentQuestion.id, newValue);
                // Don't reset transcript for text input - let it accumulate
                // But update tracking to avoid reprocessing
                lastProcessedTranscriptRef.current = currentTranscript;
              } else {
                // Not a text area and no command - just update tracking
                lastProcessedTranscriptRef.current = currentTranscript;
              }
            }
            break;
        }
      } else {
        // No command detected, try option matching first, then text input
        if (finalAssessment) {
          const currentQuestion =
            finalAssessment.questions[currentQuestionIndex];

          // First, try to match as an option if it's a dropdown/multiselect
          if (
            currentQuestion &&
            (currentQuestion.questionType === "single_choice" ||
              currentQuestion.questionType === "multiple_choice") &&
            currentQuestion.options
          ) {
            const searchText = currentNewTranscript.toLowerCase().trim();
            const matchingOption = currentQuestion.options.find(opt => {
              const label = String(opt.label).toLowerCase();
              const value = String(opt.value).toLowerCase();
              return (
                label === searchText ||
                value === searchText ||
                label.includes(searchText) ||
                searchText.includes(label)
              );
            });

            if (matchingOption) {
              if (currentQuestion.questionType === "multiple_choice") {
                const currentValue = getResponseValue(
                  currentQuestion.id
                ) as string[];
                const currentValues = Array.isArray(currentValue)
                  ? currentValue
                  : currentValue
                    ? [currentValue]
                    : [];
                const optionValue = String(matchingOption.value);
                const newValues = currentValues.includes(optionValue)
                  ? currentValues.filter(v => v !== optionValue)
                  : [...currentValues, optionValue];
                onChange(currentQuestion.id, newValues);
              } else {
                onChange(currentQuestion.id, String(matchingOption.value));
              }
              resetTranscript();
              lastProcessedTranscriptRef.current = "";
              return; // Exit early
            }
          }

          // If not matched as option, treat as text input for text areas
          if (currentQuestion && currentQuestion.questionType === "free_text") {
            const currentValue = getResponseValue(currentQuestion.id) as string;
            const newValue = currentValue
              ? `${currentValue} ${currentNewTranscript}`
              : currentNewTranscript;
            onChange(currentQuestion.id, newValue);
            // Don't reset transcript for text input - let it accumulate
            // But update tracking to avoid reprocessing
            lastProcessedTranscriptRef.current = currentTranscript;
          } else {
            // Not a text area and no command - just update tracking
            lastProcessedTranscriptRef.current = currentTranscript;
          }
        }
      }
    }, 500); // Wait 500ms for transcript to stabilize

    return () => {
      if (transcriptTimeoutRef.current) {
        clearTimeout(transcriptTimeoutRef.current);
      }
    };
  }, [
    transcript,
    voiceMode.enabled,
    finalAssessment,
    currentQuestionIndex,
    processVoiceCommand,
    onChange,
    onSaveDraft,
    onSubmit,
    onQuestionIndexChange,
    isSubmitting,
    isPreviewMode,
    resetTranscript,
    getResponseValue,
    organizationLevels,
    isLockedForViewing,
    hasAnyResponse,
    areAllRequiredFieldsFilled,
  ]);

  // Cleanup on unmount
  useEffect(() => {
    return () => {
      if (transcriptTimeoutRef.current) {
        clearTimeout(transcriptTimeoutRef.current);
      }
      if (voiceMode.enabled) {
        stopListening();
        resetTranscript();
      }
    };
  }, [voiceMode.enabled, stopListening, resetTranscript]);

  const accountName = accountNameProp ?? finalAssessment?.account?.name ?? "—";

  const VoiceModeIcon = voiceMode.enabled ? MicOff : Mic;
  const voiceModeLabel = voiceMode.enabled
    ? "Voice Mode: ON"
    : "Enable Voice Mode";

  return (
    <CommonModal
      isOpen={isOpen}
      toggle={onClose}
      size="6xl"
      showHeader={false}
      onCloseButton={onClose}
      className="h-[80vh] max-h-[90vh]"
      contentClassName="overflow-hidden flex flex-col h-full"
    >
      {!finalAssessment ? (
        <div className="flex h-full min-h-0 flex-col items-center justify-center p-8 text-center text-gray-500">
          No assessment selected.
        </div>
      ) : (
        <div className="flex h-full min-h-0 flex-col">
          <header className="flex-shrink-0 flex items-start justify-between gap-4 border-b border-gray-200 pb-3 bg-white">
            <div className="flex-1 min-w-0">
              {footerVariant === "portfolio" ? (
                <>
                  <h3 className="mb-1 text-xl font-semibold text-gray-900">
                    Assess account: {accountName}
                  </h3>
                  <p className="text-sm text-gray-500">
                    Short assessment · approx. 5 min
                  </p>
                  {isFormalFoldIn ? (
                    <div className="mt-2 rounded-md border border-amber-200 bg-amber-50 px-3 py-2 text-sm text-amber-800">
                      A formal assessment is already running for this account.
                      Your response is included in the running Formal — ESL
                      publishes.
                    </div>
                  ) : null}
                </>
              ) : (
                <>
                  <h3 className="mb-1 text-xl font-semibold text-gray-900">
                    {finalAssessment.title}
                  </h3>
                  <p className="text-sm text-gray-600">
                    Account: {accountName}
                  </p>
                </>
              )}
            </div>

            <div className="flex items-center gap-3 flex-shrink-0">
              {portfolioStatusBadge ? (
                <span className="rounded-full border border-gray-200 bg-gray-50 px-2.5 py-1 text-[11px] font-medium text-gray-600">
                  {portfolioStatusBadge}
                </span>
              ) : null}
              {voiceMode.supported && !isLockedForViewing && !isAborted ? (
                <Button
                  variant="secondary"
                  className={`flex items-center gap-2 ${
                    voiceMode.enabled
                      ? "bg-[#974ae4]/10 text-[#974ae4] border-[#974ae4]/30 shadow-[0_0_0_2px_rgba(151,74,228,0.08)]"
                      : ""
                  }`}
                  onClick={() => voiceMode.onToggle(!voiceMode.enabled)}
                  icon={<VoiceModeIcon className="w-5 h-5" />}
                >
                  {voiceModeLabel}
                </Button>
              ) : null}

              <button
                type="button"
                className="p-2 hover:bg-gray-100 rounded-lg transition-colors"
                onClick={onClose}
                aria-label="Close"
              >
                <X className="w-5 h-5 text-gray-500" />
              </button>
            </div>
          </header>

          {isPortfolioValidationLoading ? (
            <div className="flex min-h-0 flex-1 items-center justify-center">
              <div className="flex flex-col items-center gap-3">
                <Loader2
                  className="h-8 w-8 animate-spin text-purple-600"
                  aria-label="Validating assessment eligibility"
                />
                <p className="text-sm text-gray-500">
                  Checking prior assessments…
                </p>
              </div>
            </div>
          ) : (
            <>
              {reassessBanner &&
              !isLockedForViewing &&
              !isDraftSession &&
              !portfolioBlockedMessage ? (
                <div className="mx-0 mt-4 flex gap-2.5 rounded-lg border border-violet-200 bg-violet-50 px-4 py-3">
                  <AlertCircle className="mt-0.5 h-5 w-5 shrink-0 text-violet-600" />
                  <p className="m-0 text-sm leading-relaxed text-violet-950">
                    {renderReassessBannerMessage(reassessBanner)}
                  </p>
                </div>
              ) : null}

              {remainderBanner &&
              !isLockedForViewing &&
              !isFormalFoldIn &&
              !portfolioBlockedMessage ? (
                <div className="mx-0 mt-4 flex gap-2.5 rounded-lg border border-amber-200 bg-amber-50 px-4 py-3">
                  <AlertCircle className="mt-0.5 h-5 w-5 shrink-0 text-amber-600" />
                  <p className="m-0 text-sm leading-relaxed text-amber-950">
                    No assessment window is open for this account. Your input
                    stays private and counts{" "}
                    {remainderBanner.countsOn ? (
                      <>
                        on{" "}
                        <strong>
                          {
                            formatAssessmentDateTimePartsUtc(
                              remainderBanner.countsOn,
                              "DMY_SHORT"
                            ).date
                          }
                        </strong>
                      </>
                    ) : (
                      "at the next window close"
                    )}{" "}
                    if it is still current then.
                  </p>
                </div>
              ) : null}

              {portfolioBlockedMessage ? (
                <div
                  className="mx-0 mt-4 flex flex-col gap-3 rounded-lg border border-amber-200 bg-amber-50 px-4 py-3 sm:flex-row sm:items-center sm:justify-between sm:gap-4"
                  role="alert"
                >
                  <div className="flex min-w-0 items-center gap-2.5">
                    <AlertCircle
                      className="h-5 w-5 shrink-0 text-amber-600"
                      aria-hidden
                    />
                    <p className="m-0 text-sm leading-snug text-amber-950">
                      {portfolioBlockedMessage}
                    </p>
                  </div>
                  {onTakeFormalAssessment ? (
                    <Button
                      type="button"
                      variant="primary"
                      className="shrink-0 self-stretch sm:self-center"
                      onClick={onTakeFormalAssessment}
                      disabled={
                        isSubmitting ||
                        isPortfolioValidationLoading ||
                        isTakeFormalAssessmentLoading
                      }
                      loading={isTakeFormalAssessmentLoading}
                    >
                      Take Formal Assessment
                    </Button>
                  ) : null}
                </div>
              ) : null}

              {isCarryForwardMode && !isPortfolioBlocked ? (
                <div className="mx-0 mt-3 flex gap-2.5 rounded-lg border border-emerald-200 bg-emerald-50 px-4 py-3">
                  <AlertCircle className="mt-0.5 h-5 w-5 shrink-0 text-emerald-600" />
                  <p className="m-0 text-sm leading-relaxed text-emerald-950">
                    <strong>Carry Forward mode:</strong> level and rationale are
                    read-only.
                  </p>
                </div>
              ) : null}

              {voiceMode.enabled && voiceMode.showInstructions ? (
                <section
                  className="flex-shrink-0 bg-gradient-to-r from-[#974ae4]/10 to-[#974ae4]/5 border-b border-[#974ae4]/20 p-4"
                  aria-live="polite"
                >
                  <div className="mb-3">
                    <div className="flex items-center gap-3 flex-wrap">
                      <span className="text-sm font-medium text-gray-900">
                        🎤 Voice Mode Active{" "}
                        {isListening ? "— Listening..." : "— Ready"}
                      </span>
                      {finalAssessment &&
                        finalAssessment.questions.length > 0 && (
                          <span className="bg-[#974ae4]/15 text-[#974ae4] px-3 py-1 rounded-xl text-sm font-semibold">
                            Question {currentQuestionIndex + 1} of{" "}
                            {finalAssessment.questions.length}
                          </span>
                        )}
                    </div>
                    {speechError && (
                      <span className="ml-2.5 text-[#d32f2f] text-sm block mt-2">
                        {speechError}
                        <button
                          onClick={clearError}
                          className="ml-2 bg-transparent border-none text-inherit cursor-pointer underline"
                        >
                          Dismiss
                        </button>
                      </span>
                    )}
                  </div>
                  <ul className="list-disc list-inside space-y-1 text-sm text-gray-700 mb-2">
                    <li>
                      <strong>Levels:</strong> &quot;level zero&quot;,
                      &quot;level one&quot;, …
                    </li>
                    <li>
                      <strong>Ratings:</strong> &quot;one&quot;,
                      &quot;two&quot;, &quot;three&quot;, &quot;four&quot;,
                      &quot;five&quot;
                    </li>
                    <li>
                      <strong>Dropdown/Multiselect:</strong> Say the option
                      name, or &quot;select option one&quot;, &quot;first
                      option&quot;, etc.
                    </li>
                    <li>
                      <strong>Navigation:</strong> &quot;next&quot;,
                      &quot;previous&quot;, &quot;back&quot;
                    </li>
                    <li>
                      <strong>Actions:</strong> &quot;save draft&quot;,
                      &quot;submit&quot;
                    </li>
                    <li>
                      <strong>Clear:</strong> &quot;clear&quot;,
                      &quot;delete&quot;, &quot;reset&quot;
                    </li>
                  </ul>
                  <p className="text-xs text-gray-600 italic">
                    💡 Speak your answers—textareas capture your speech
                    instantly.
                  </p>
                </section>
              ) : null}

              <section className="flex-1 min-h-0 overflow-y-auto space-y-6 mt-3">
                {finalAssessment.questions?.length ? (
                  (() => {
                    const hasCategories = finalAssessment.questions.some(
                      q => q.categoryName
                    );
                    if (hasCategories) {
                      const grouped = finalAssessment.questions.reduce(
                        (acc: Record<string, AssessmentQuestion[]>, q) => {
                          const k = q.categoryName ?? "Uncategorized";
                          (acc[k] ??= []).push(q);
                          return acc;
                        },
                        {}
                      );

                      let questionIndex = 0;
                      return Object.entries(grouped).map(
                        ([categoryName, qs]) => (
                          <div key={categoryName} className="space-y-4">
                            <h4 className="text-lg font-semibold text-gray-900 border-b border-gray-200 pb-2">
                              {categoryName}
                            </h4>
                            {qs.map(q => {
                              const isActive =
                                voiceMode.enabled &&
                                questionIndex === currentQuestionIndex;
                              questionIndex++;
                              return (
                                <article
                                  key={q.id}
                                  className={`relative p-4 rounded-lg border transition-all ${
                                    isActive
                                      ? "border-2 border-[#a855f7] bg-[#974ae4]/5"
                                      : "border-gray-200 bg-white"
                                  }`}
                                >
                                  {isActive && (
                                    <span className="absolute -top-3 left-6 bg-[#974ae4] text-white px-3 py-1 rounded-xl text-[11px] font-semibold tracking-wide shadow-[0_2px_8px_rgba(151,74,228,0.3)] z-10">
                                      🎤 Active
                                    </span>
                                  )}
                                  <label
                                    className={`block text-base font-medium mb-3 ${
                                      isActive
                                        ? "text-[#974ae4] font-bold"
                                        : "text-gray-900"
                                    }`}
                                  >
                                    {q.question}
                                    {q.required ? (
                                      <span className="text-red-500 ml-1">
                                        *
                                      </span>
                                    ) : null}
                                  </label>
                                  <InputRenderer
                                    question={q}
                                    value={
                                      getResponseValue(q.id) as
                                        | string
                                        | string[]
                                        | undefined
                                    }
                                    onChange={v => onChange(q.id, v)}
                                    disabled={
                                      questionsDisabled ||
                                      isQuestionReadOnly(q.id)
                                    }
                                    organizationLevels={organizationLevels}
                                    onGuidanceChange={setActiveGuidance}
                                    placeholderOverride={
                                      q.questionType === "free_text"
                                        ? activeGuidance
                                        : undefined
                                    }
                                  />
                                </article>
                              );
                            })}
                          </div>
                        )
                      );
                    }

                    return finalAssessment.questions.map((q, idx) => {
                      const isActive =
                        voiceMode.enabled && idx === currentQuestionIndex;
                      return (
                        <article
                          key={q.id}
                          className={`relative p-4 rounded-lg border transition-all ${
                            isActive
                              ? "border-2 border-[#a855f7] bg-[#974ae4]/5"
                              : "border-gray-200 bg-white"
                          }`}
                        >
                          {isActive && (
                            <span className="absolute -top-3 left-6 bg-[#974ae4] text-white px-3 py-1 rounded-xl text-[11px] font-semibold tracking-wide shadow-[0_2px_8px_rgba(151,74,228,0.3)] z-50">
                              🎤 Active
                            </span>
                          )}
                          <label
                            className={`block text-base font-medium mb-3 ${
                              isActive
                                ? "text-[#974ae4] font-bold"
                                : "text-gray-900"
                            }`}
                          >
                            {q.question}
                            {q.required ? (
                              <span className="text-red-500 ml-1">*</span>
                            ) : null}
                          </label>
                          <InputRenderer
                            question={q}
                            value={
                              getResponseValue(q.id) as
                                | string
                                | string[]
                                | undefined
                            }
                            onChange={v => onChange(q.id, v)}
                            disabled={
                              questionsDisabled || isQuestionReadOnly(q.id)
                            }
                            organizationLevels={organizationLevels}
                            onGuidanceChange={setActiveGuidance}
                            placeholderOverride={
                              q.questionType === "free_text"
                                ? activeGuidance
                                : undefined
                            }
                          />
                        </article>
                      );
                    });
                  })()
                ) : (
                  <div className="p-8 text-center text-gray-500">
                    No questions available for this assessment.
                  </div>
                )}

                {isCarryForwardMode && onCarryForwardNoteChange ? (
                  <div className="rounded-lg border border-emerald-200 bg-emerald-50/50 p-4">
                    <label className="mb-2 block text-sm font-medium text-gray-900">
                      Carry-forward note{" "}
                      <span className="font-normal text-gray-500">
                        (optional, for context on this confirmation)
                      </span>
                    </label>
                    <TextArea
                      name="carryForwardNote"
                      value={carryForwardNote}
                      onChange={e => onCarryForwardNoteChange(e.target.value)}
                      rows={3}
                      placeholder="e.g. Confirmed with customer success — no material change since last assessment."
                    />
                  </div>
                ) : null}
              </section>
            </>
          )}

          <footer className="flex-shrink-0 flex items-center justify-between gap-4 border-t border-gray-200 pt-3 bg-white">
            {footerVariant === "portfolio" && isContinuedFlow && onFormBack ? (
              <Button
                variant="outline-primary"
                onClick={onFormBack}
                disabled={isSubmitting}
              >
                Back
              </Button>
            ) : (
              <Button
                variant="outline-primary"
                onClick={onClose}
                disabled={isSubmitting}
              >
                {footerVariant === "portfolio" ? "Cancel" : "Close"}
              </Button>
            )}
            {!isPreviewMode && (
              <div className="flex items-center gap-3 ml-auto">
                {isLockedForViewing ? (
                  <span className="text-sm text-green-600 font-medium">
                    {isPublished
                      ? "Assessment published"
                      : isPrePublish
                        ? "Due date passed — responses locked"
                        : "Assessment completed and submitted"}
                  </span>
                ) : footerVariant === "portfolio" ? (
                  isContinuedFlow ? (
                    <>
                      <Button
                        variant="outline-primary"
                        onClick={onSaveDraft}
                        disabled={
                          isSubmitting ||
                          isPortfolioBlocked ||
                          isPortfolioValidationLoading ||
                          !hasAnyResponse
                        }
                        loading={
                          isSubmitting && portfolioSubmitAction === "draft"
                        }
                        title={
                          !hasAnyResponse
                            ? "Answer at least one question before saving a draft"
                            : undefined
                        }
                      >
                        Save draft
                      </Button>
                      <Button
                        variant="primary"
                        onClick={onSubmit}
                        disabled={
                          isSubmitting ||
                          isPortfolioBlocked ||
                          isPortfolioValidationLoading ||
                          !areAllRequiredFieldsFilled
                        }
                        loading={
                          isSubmitting && portfolioSubmitAction === "submit"
                        }
                      >
                        Submit Assessment
                      </Button>
                    </>
                  ) : (
                    <>
                      <Button
                        variant="outline-primary"
                        onClick={onSaveDraft}
                        disabled={
                          isSubmitting ||
                          isPortfolioBlocked ||
                          isPortfolioValidationLoading ||
                          !hasAnyResponse
                        }
                        loading={
                          isSubmitting && portfolioSubmitAction === "draft"
                        }
                        title={
                          !hasAnyResponse
                            ? "Answer at least one question before saving a draft"
                            : undefined
                        }
                      >
                        Save draft
                      </Button>
                      {onGoToMyAssessments ? (
                        <Button
                          variant="outline-primary"
                          onClick={onGoToMyAssessments}
                          disabled={
                            isSubmitting ||
                            isPortfolioBlocked ||
                            isPortfolioValidationLoading ||
                            !canGoToMyAssessments
                          }
                          loading={
                            isSubmitting && portfolioSubmitAction === "navigate"
                          }
                          title={
                            isPortfolioBlocked
                              ? (portfolioBlockedMessage ?? undefined)
                              : !canGoToMyAssessments
                                ? "No draft or assigned assessment for this account"
                                : undefined
                          }
                        >
                          Go to My Assessments
                        </Button>
                      ) : null}
                      <Button
                        variant="primary"
                        onClick={onSubmit}
                        disabled={
                          isSubmitting ||
                          isPortfolioBlocked ||
                          isPortfolioValidationLoading ||
                          !areAllRequiredFieldsFilled
                        }
                        loading={
                          isSubmitting && portfolioSubmitAction === "submit"
                        }
                        title={
                          isPortfolioBlocked
                            ? (portfolioBlockedMessage ?? undefined)
                            : !areAllRequiredFieldsFilled
                              ? "Please fill all required fields (marked with *) before submitting"
                              : ""
                        }
                      >
                        Submit Assessment
                      </Button>
                    </>
                  )
                ) : (
                  <>
                    <Button
                      variant="outline-primary"
                      onClick={onSaveDraft}
                      disabled={isSubmitting || !hasAnyResponse}
                      title={
                        !hasAnyResponse
                          ? "Answer at least one question before saving a draft"
                          : undefined
                      }
                    >
                      {isSubmitting ? "Saving…" : "Save Draft"}
                    </Button>
                    <Button
                      variant="primary"
                      onClick={onSubmit}
                      disabled={isSubmitting || !areAllRequiredFieldsFilled}
                      title={
                        !areAllRequiredFieldsFilled
                          ? "Please fill all required fields (marked with *) before submitting"
                          : ""
                      }
                    >
                      {isSubmitting ? "Submitting…" : "Submit Assessment"}
                    </Button>
                  </>
                )}
              </div>
            )}
          </footer>
        </div>
      )}
    </CommonModal>
  );
};

export default ReviewAssessmentModal;
